import { Logger } from "@viamap/viamap2-common";
import { buildFileFromDataUrl, sanitizeFilename, uploadOverlayImage, deleteOverlayImage, extractS3KeyFromUrl, downloadOverlayImage, blobToDataUrl } from "./ANNImageOverlayStorage";
import { LayerType } from "src/common/managers/Types";
import {
   AnnotationType,
   AnnotationContextType,
   AnnotationQuality,
   type ANNAnnotationRecord,
   type ANNLayerSnapshot,
   type ANNAnnotationHistoryEntry,
   type SaveAnnotationInput,
} from "./ANNTypes";
import type { Geometry } from "geojson";
import { getAnnotationPersistence } from "./ANNAnnotationPersistence";
function extractGeometriesFromLayers(layers: ANNLayerSnapshot[]): Geometry[] {
   const geometries: Geometry[] = [];

   for (const layer of layers) {
      const layerGeometries: Geometry[] = [];

      if (layer.geoJson?.features) {
         for (const feature of layer.geoJson.features) {
            if (feature.geometry) {
               layerGeometries.push(feature.geometry);
            }
         }
      }

      if (layer.imageOverlay?.coordinates && layer.imageOverlay.coordinates.length > 0) {
         const polygon: Geometry = {
            type: "Polygon",
            coordinates: [layer.imageOverlay.coordinates]
         };
         layerGeometries.push(polygon);
      }

      if (layerGeometries.length === 0) {
         continue;
      } else if (layerGeometries.length === 1) {
         geometries.push(layerGeometries[0]);
      } else {
         const geometryTypes = new Set(layerGeometries.map(g => g.type));

         if (geometryTypes.size === 1) {
            const type = layerGeometries[0].type;
            if (type === "Polygon") {
               geometries.push({
                  type: "MultiPolygon",
                  coordinates: layerGeometries.map(g => (g as any).coordinates)
               });
            } else if (type === "LineString") {
               geometries.push({
                  type: "MultiLineString",
                  coordinates: layerGeometries.map(g => (g as any).coordinates)
               });
            } else if (type === "Point") {
               geometries.push({
                  type: "MultiPoint",
                  coordinates: layerGeometries.map(g => (g as any).coordinates)
               });
            } else {
               geometries.push({
                  type: "GeometryCollection",
                  geometries: layerGeometries
               });
            }
         } else {
            geometries.push({
               type: "GeometryCollection",
               geometries: layerGeometries
            });
         }
      }
   }

   return geometries;
}

function normalizeAnnotation(hit: any): ANNAnnotationRecord {
   const source = hit?._source ?? hit ?? {};
   const hasLegacyVerification = source.verifiedQuality !== undefined && source.verifiedQuality !== null;
   const verified = source.verified !== undefined ? source.verified : hasLegacyVerification;

   return {
      id: source.id || hit?._id || (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}`),
      documentId: source.documentId,
      bfeNumber: source.bfeNumber,
      datoloebenr: source.datoloebenr || source.datoløbenr,
      contextType: source.contextType || AnnotationContextType.Servitut,
      organizationId: source.organizationId,
      createdAt: source.createdAt,
      createdBy: source.createdBy,
      createdByName: source.createdByName,
      title: source.title,
      comment: source.comment,
      history: source.history || [],
      layers: source.layers || [],
      note: source.note,
      document: source.document,
      type: source.type || AnnotationType.Stedfaestelse,
      annotationType: source.annotationType,
      quality: source.quality,
      verified,
      lastEditedAt: source.lastEditedAt,
      lastEditedBy: source.lastEditedBy,
      lastEditedByName: source.lastEditedByName,
      verifiedQuality: source.verifiedQuality,
      verifiedBy: source.verifiedBy,
      verifiedByName: source.verifiedByName,
      verifiedAt: source.verifiedAt,
   };
}
async function persistLayerSnapshots(layers: ANNLayerSnapshot[] = []): Promise<ANNLayerSnapshot[]> {
   return Promise.all(
      layers.map(async (layer, index) => {
         if (layer.type === LayerType.ImageOverlay && layer.geoJson?.features?.length) {
            const firstFeature = layer.geoJson.features[0];
            const imgUrl = firstFeature?.properties?._imgUrl;
            if (!imgUrl) {
               return layer;
            }

            let dataUrl = imgUrl;
            if (imgUrl.startsWith("http") && (imgUrl.includes("s3") || imgUrl.includes("amazonaws"))) {
               try {
                  const s3Key = extractS3KeyFromUrl(imgUrl);
                  if (s3Key) {
                     const { blob } = await downloadOverlayImage(s3Key);
                     dataUrl = await blobToDataUrl(blob);
                  } else {
                     const response = await fetch(imgUrl);
                     if (!response.ok) {
                        console.warn(`Failed to fetch image from ${imgUrl}, skipping upload`);
                        return layer;
                     }
                     const blob = await response.blob();
                     dataUrl = await blobToDataUrl(blob);
                  }
               } catch (err) {
                  if (!imgUrl.startsWith("data:")) {
                     return layer;
                  }
                  dataUrl = imgUrl;
               }
            }

            if (!dataUrl.startsWith("data:")) {
               return layer;
            }

            const baseName =
               layer.datasetName ||
               layer.filename ||
               `overlay-${layer.layerId ?? index + 1}`;
            const file = await buildFileFromDataUrl(dataUrl, sanitizeFilename(baseName));
            const { url } = await uploadOverlayImage(file);
            const updatedGeoJson = structuredClone(layer.geoJson);
            if (updatedGeoJson.features[0]?.properties) {
               updatedGeoJson.features[0].properties._imgUrl = url;
            }
            return {
               ...layer,
               filename: layer.filename || file.name,
               geoJson: updatedGeoJson,
            };
         }
         if (layer.type === LayerType.ImageOverlay && layer.imageOverlay) {
            let dataUrl = layer.imageOverlay.dataUrl;

            if (layer.imageOverlay.s3Key && !dataUrl) {
               try {
                  const { blob } = await downloadOverlayImage(layer.imageOverlay.s3Key);
                  dataUrl = await blobToDataUrl(blob);
               } catch (err) {
                  return layer;
               }
            }

            if (!dataUrl) {
               return layer;
            }

            const baseName =
               layer.datasetName ||
               layer.filename ||
               `overlay-${layer.layerId ?? index + 1}`;
            const file = await buildFileFromDataUrl(dataUrl, sanitizeFilename(baseName));
            const { key, url } = await uploadOverlayImage(file);
            return {
               ...layer,
               filename: layer.filename || file.name,
               imageOverlay: {
                  ...layer.imageOverlay,
                  dataUrl: undefined,
                  s3Key: key,
                  s3Url: url,
               },
            };
         }
         return layer;
      })
   );
}
export class ANNManager {
   static async listAnnotations(
      contextType: AnnotationContextType,
      identifier: string | number | undefined,
      organizationId?: string,
      filterGeometry?: Geometry
   ): Promise<ANNAnnotationRecord[]> {
      if (identifier === undefined || identifier === null || identifier === "") {
         return [];
      }
      if (!organizationId) {
         console.warn("listAnnotations called without organizationId - using base index");
      }
      const must: Array<Record<string, unknown>> = [];
      if (organizationId) {
         must.push({ match_phrase: { organizationId: organizationId } });
      }
      if (contextType === AnnotationContextType.Servitut) {
         must.push({ match_phrase: { documentId: identifier } });
         must.push({
            bool: {
               should: [
                  { match_phrase: { contextType: AnnotationContextType.Servitut } },
                  { bool: { must_not: { exists: { field: "contextType" } } } }
               ],
               minimum_should_match: 1
            }
         });
      } else if (contextType === AnnotationContextType.Ejendom) {
         must.push({ match_phrase: { contextType: AnnotationContextType.Ejendom } });
         const bfeNumberStr = typeof identifier === "number" ? String(identifier) : identifier;
         must.push({ match_phrase: { bfeNumber: bfeNumberStr } });
      }

      if (filterGeometry) {
         must.push({
            geo_shape: {
               geometries: {
                  shape: filterGeometry,
                  relation: "intersects"
               }
            }
         });
      }

      const query = {
         size: 250,
         sort: [{ createdAt: { order: "desc" } }],
         query: {
            bool: { must }
         },
      };
      try {
         const persistence = getAnnotationPersistence(organizationId);
         await persistence.ensureIndexExists();
         const result = await persistence.getEs().doQuery(query);
         const hits = result?.hits?.hits || [];
         return hits.map((hit: any) => normalizeAnnotation(hit));
      } catch (err: any) {
         const errorMessage = err?.message || String(err);
         if (errorMessage.includes("index_not_found") || errorMessage.includes("no such index")) {
            try {
               const persistence = getAnnotationPersistence(organizationId);
               await persistence.ensureIndexExists();
               const result = await persistence.getEs().doQuery(query);
               const hits = result?.hits?.hits || [];
               return hits.map((hit: any) => normalizeAnnotation(hit));
            } catch (retryErr: any) {
               throw retryErr;
            }
         } else {
            console.error("Error querying annotations:", err, "Query:", JSON.stringify(query, null, 2), "ContextType:", contextType, "Identifier:", identifier, "OrganizationId:", organizationId);
            throw err;
         }
      }
   }
   static async listAllAnnotations(
      contextType: AnnotationContextType,
      organizationId?: string,
      filterGeometry?: Geometry
   ): Promise<ANNAnnotationRecord[]> {
      if (!organizationId) {
      }
      const must: Array<Record<string, unknown>> = [];
      if (organizationId) {
         must.push({ match_phrase: { organizationId: organizationId } });
      }
      if (contextType === AnnotationContextType.Servitut) {
         must.push({
            bool: {
               should: [
                  { match_phrase: { contextType: AnnotationContextType.Servitut } },
                  { bool: { must_not: { exists: { field: "contextType" } } } }
               ],
               minimum_should_match: 1
            }
         });
      } else if (contextType === AnnotationContextType.Ejendom) {
         must.push({ match_phrase: { contextType: AnnotationContextType.Ejendom } });
      }

      if (filterGeometry) {
         must.push({
            geo_shape: {
               geometries: {
                  shape: filterGeometry,
                  relation: "intersects"
               }
            }
         });
      }

      const query = {
         size: 1000,
         sort: [{ createdAt: { order: "desc" } }],
         query: {
            bool: { must }
         },
      };
      try {
         const persistence = getAnnotationPersistence(organizationId);
         await persistence.ensureIndexExists();
         const result = await persistence.getEs().doQuery(query);
         const hits = result?.hits?.hits || [];
         return hits.map((hit: any) => normalizeAnnotation(hit));
      } catch (err: any) {
         const errorMessage = err?.message || String(err);
         if (errorMessage.includes("index_not_found") || errorMessage.includes("no such index")) {
            try {
               const persistence = getAnnotationPersistence(organizationId);
               await persistence.ensureIndexExists();
               const result = await persistence.getEs().doQuery(query);
               const hits = result?.hits?.hits || [];
               return hits.map((hit: any) => normalizeAnnotation(hit));
            } catch (retryErr: any) {
               console.error("Error querying all annotations after index creation:", retryErr, "Query:", JSON.stringify(query, null, 2), "ContextType:", contextType, "OrganizationId:", organizationId);
               throw retryErr;
            }
         } else {
            throw err;
         }
      }
   }
   static async listUnverifiedAnnotations(
      contextType: AnnotationContextType,
      organizationId?: string
   ): Promise<ANNAnnotationRecord[]> {
      if (!organizationId) {
         console.warn("listUnverifiedAnnotations called without organizationId - using base index");
      }
      const must: Array<Record<string, unknown>> = [];
      if (organizationId) {
         must.push({ match_phrase: { organizationId: organizationId } });
      }
      if (contextType === AnnotationContextType.Servitut) {
         must.push({
            bool: {
               should: [
                  { match_phrase: { contextType: AnnotationContextType.Servitut } },
                  { bool: { must_not: { exists: { field: "contextType" } } } }
               ],
               minimum_should_match: 1
            }
         });
      } else if (contextType === AnnotationContextType.Ejendom) {
         must.push({ match_phrase: { contextType: AnnotationContextType.Ejendom } });
      }
      const query = {
         size: 500,
         sort: [{ createdAt: { order: "desc" } }],
         query: {
            bool: {
               must,
               must_not: [
                  { exists: { field: "verifiedQuality" } }
               ]
            }
         },
      };
      try {
         const persistence = getAnnotationPersistence(organizationId);
         await persistence.ensureIndexExists();
         const result = await persistence.getEs().doQuery(query);
         const hits = result?.hits?.hits || [];
         return hits.map((hit: any) => normalizeAnnotation(hit));
      } catch (err: any) {
         const errorMessage = err?.message || String(err);
         if (errorMessage.includes("index_not_found") || errorMessage.includes("no such index")) {
            try {
               const persistence = getAnnotationPersistence(organizationId);
               await persistence.ensureIndexExists();
               const result = await persistence.getEs().doQuery(query);
               const hits = result?.hits?.hits || [];
               return hits.map((hit: any) => normalizeAnnotation(hit));
            } catch (retryErr: any) {
               throw retryErr;
            }
         } else {
            console.error("Error querying unverified annotations:", err, "Query:", JSON.stringify(query, null, 2), "ContextType:", contextType, "OrganizationId:", organizationId);
            throw err;
         }
      }
   }
   static async saveAnnotation(input: SaveAnnotationInput): Promise<ANNAnnotationRecord> {
      if (!input.organizationId) {
         throw new Error("Organization ID is required to save annotations.");
      }
      if (input.contextType === AnnotationContextType.Servitut && !input.documentId) {
         throw new Error("documentId is required for servitut annotations.");
      }
      if (input.contextType === AnnotationContextType.Ejendom && !input.bfeNumber) {
         throw new Error("bfeNumber is required for ejendom annotations.");
      }
      if (!input.title || !input.title.trim()) {
         throw new Error("Title is required for annotations.");
      }
      const id = input.id || (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `ann-${Date.now()}`);
      const layers = input.type === AnnotationType.Stedfaestelse ? await persistLayerSnapshots(input.layers ?? []) : [];

      const geometries = extractGeometriesFromLayers(layers);

      const now = new Date().toISOString();
      const isEdit = !!input.originalCreatedAt || !!input.id; // If we have original creation info or id, this is an edit
      const persistence = getAnnotationPersistence(input.organizationId, input.userId);
      let existingHistory: ANNAnnotationHistoryEntry[] = [];
      if (input.id) {
         try {
            await persistence.ensureIndexExists();
            const existing = await persistence.getObject(input.id);
            const normalized = normalizeAnnotation(existing);
            existingHistory = normalized.history || [];
         } catch (err: any) {
            const errorMessage = err?.message || String(err);
            if (!errorMessage.includes("not_found") && !errorMessage.includes("does not exist")) {
               throw err;
            }
         }
      }
      const historyEntry: ANNAnnotationHistoryEntry = {
         timestamp: now,
         userId: input.userId,
         userName: input.userName,
         quality: input.quality,
         verified: input.verified || false,
         comment: input.historyComment?.trim() || undefined,
      };

      const record: ANNAnnotationRecord = {
         id,
         documentId: input.documentId,
         bfeNumber: input.bfeNumber !== undefined ? (typeof input.bfeNumber === "number" ? String(input.bfeNumber) : input.bfeNumber) : undefined,
         datoloebenr: input.datoloebenr,
         contextType: input.contextType,
         organizationId: input.organizationId,
         createdAt: isEdit ? (input.originalCreatedAt || now) : now,
         createdBy: isEdit ? (input.originalCreatedBy || input.userId) : input.userId,
         createdByName: isEdit ? (input.originalCreatedByName || input.userName) : input.userName,
         title: input.title.trim(),
         comment: input.comment,
         history: [...existingHistory, historyEntry],
         layers,
         note: input.type === AnnotationType.Note ? input.note : undefined,
         document: input.type === AnnotationType.Document ? input.document : undefined,
         type: input.type,
         annotationType: input.annotationType,
         quality: input.quality,
         verified: input.verified || false,
         lastEditedAt: now,
         lastEditedBy: input.userId,
         lastEditedByName: input.userName,
      };

      const esRecord: any = {
         ...record,
      };

      if (geometries.length > 0) {
         esRecord.geometries = geometries;
      }

      await persistence.ensureIndexExists();
      try {
         await persistence.saveObject(esRecord);
         Logger.logAction(
            "ANNManager",
            isEdit ? "updateAnnotation" : "createAnnotation",
            JSON.stringify({
               id,
               organizationId: input.organizationId,
               contextType: input.contextType,
               type: input.type
            })
         );
      } catch (err: any) {
         const errorMessage = err?.message || String(err);
         if (errorMessage.includes("index_not_found") || errorMessage.includes("no such index")) {
            await persistence.ensureIndexExists();
            await persistence.saveObject(esRecord);
         } else {
            throw err;
         }
      }
      return record;
   }
   static async verifyAnnotationQuality(
      id: string,
      quality: AnnotationQuality,
      organizationId: string,
      userId: string,
      userName: string
   ): Promise<void> {
      if (!organizationId) {
         throw new Error("Organization ID is required to verify annotation quality.");
      }
      const persistence = getAnnotationPersistence(organizationId, userId);
      await persistence.ensureIndexExists();
      let existingRecord: ANNAnnotationRecord;
      try {
         const existing = await persistence.getObject(id);
         existingRecord = normalizeAnnotation(existing);
      } catch (err: any) {
         const errorMessage = err?.message || String(err);
         if (errorMessage.includes("index_not_found") || errorMessage.includes("no such index")) {
            await persistence.ensureIndexExists();
            const existing = await persistence.getObject(id);
            existingRecord = normalizeAnnotation(existing);
         } else {
            throw new Error(`Failed to load annotation: ${errorMessage}`);
         }
      }
      const updatedRecord: ANNAnnotationRecord = {
         ...existingRecord,
         verifiedQuality: quality,
         verifiedBy: userId,
         verifiedByName: userName,
         verifiedAt: new Date().toISOString(),
         verified: true,
         history: [
            ...(existingRecord.history || []),
            {
               timestamp: new Date().toISOString(),
               userId,
               userName,
               quality,
               verified: true,
            }
         ],
      };
      try {
         await persistence.saveObject(updatedRecord);
         Logger.logAction(
            "ANNManager",
            "verifyAnnotationQuality",
            JSON.stringify({ id, quality, organizationId })
         );
      } catch (err: any) {
         const errorMessage = err?.message || String(err);
         if (errorMessage.includes("index_not_found") || errorMessage.includes("no such index")) {
            await persistence.ensureIndexExists();
            await persistence.saveObject(updatedRecord);
         } else {
            throw err;
         }
      }
   }
   static async deleteAnnotation(id: string, organizationId?: string): Promise<void> {
      if (!organizationId) {
         throw new Error("Organization ID is required to delete annotations.");
      }
      const persistence = getAnnotationPersistence(organizationId);
      await persistence.ensureIndexExists();
      let annotation: ANNAnnotationRecord | null = null;
      try {
         const existing = await persistence.getObject(id);
         annotation = normalizeAnnotation(existing);
      } catch (err: any) {
         const errorMessage = err?.message || String(err);
         if (errorMessage.includes("index_not_found") || errorMessage.includes("no such index")) {
            return;
         }
         if (errorMessage.includes("not_found") || errorMessage.includes("does not exist")) {
            try {
               await persistence.deleteObject(id);
            } catch (deleteErr: any) {
               const deleteErrorMsg = deleteErr?.message || String(deleteErr);
               if (!deleteErrorMsg.includes("not_found") && !deleteErrorMsg.includes("does not exist")) {
                  throw deleteErr;
               }
            }
            return;
         }
         throw err;
      }
      const s3DeletionPromises: Promise<void>[] = [];
      if (annotation.document?.s3Key) {
         s3DeletionPromises.push(
            deleteOverlayImage(annotation.document.s3Key).catch((err: any) => {
            })
         );
      }
      if (annotation.layers && annotation.layers.length > 0) {
         for (const layer of annotation.layers) {
            if (layer.type === LayerType.ImageOverlay) {
               if (layer.imageOverlay?.s3Key) {
                  s3DeletionPromises.push(
                     deleteOverlayImage(layer.imageOverlay.s3Key).catch((err: any) => {
                        console.warn(`Failed to delete S3 overlay file ${layer.imageOverlay?.s3Key}:`, err);
                     })
                  );
               }
               else if (layer.geoJson?.features?.[0]?.properties?._imgUrl) {
                  const imgUrl = layer.geoJson.features[0].properties._imgUrl;
                  const s3Key = extractS3KeyFromUrl(imgUrl);
                  if (s3Key) {
                     s3DeletionPromises.push(
                        deleteOverlayImage(s3Key).catch((err: any) => {
                        })
                     );
                  }
               }
            }
         }
      }
      await Promise.allSettled(s3DeletionPromises);
      try {
         await persistence.deleteObject(id);
         Logger.logAction(
            "ANNManager",
            "deleteAnnotation",
            JSON.stringify({ id, organizationId })
         );
      } catch (err: any) {
         const errorMessage = err?.message || String(err);
         if (errorMessage.includes("index_not_found") || errorMessage.includes("no such index")) {
            return;
         }
         throw err;
      }
   }
}
